import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import { formatDistanceToNow } from 'date-fns';
import { dataAPI } from '../../helpers/api_service';

const NotificationsPanel = ({ notifications: notificationsData }) => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();

  // Process notifications data from API
  const notifications = notificationsData || [];

  // Function to format time ago
  const getTimeAgo = (sentTime) => {
    try {
      const date = new Date(sentTime);
      const distance = formatDistanceToNow(date, { addSuffix: true });
      // Remove "about" keyword from timing
      return distance.replace(/^about\s/, '');
    } catch (error) {
      return 'Just now';
    }
  };

  // Function to generate avatar initials from sender name
  const getAvatarInitials = (sender) => {
    if (!sender?.full_name) return 'NA';
    return sender.full_name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Handle notification click with smart navigation (same as topbar notifications)
  const handleNotificationClick = async (notification) => {
    console.log('🎯 Notification clicked:', notification);
    
    // Mark notification as read if not already read
    if (notification.id && !notification.is_read) {
      try {
        // Disable global loading indicator for this API call to avoid popup overlay
        await dataAPI.markNotificationRead(notification.id, { showLoading: false });
        console.log(`✅ Marked notification ${notification.id} as read`);
      } catch (error) {
        console.error('❌ Error marking notification as read:', error);
        // Continue with navigation even if marking as read fails
      }
    }
    
    // Check if notification has related entity information
    if (notification.related_entity_id && notification.related_entity_type) {
      
      switch (notification.related_entity_type.toLowerCase()) {
        case 'task':
          // Navigate to task details page
          console.log(`📋 Navigating to task: ${notification.related_entity_id}`);
          navigate(`/project/1/task/${notification.related_entity_id}`, {
            state: {
              fromNotification: true,
              notificationId: notification.id
            }
          });
          break;
          
        case 'project':
        case 'projects':
          // Navigate to projects page
          console.log(`📁 Navigating to projects page`);
          navigate('/projects', {
            state: {
              fromNotification: true,
              notificationId: notification.id,
              focusProjectId: notification.related_entity_id
            }
          });
          break;
          
        case 'document':
          // Navigate to documents page
          console.log(`📄 Navigating to documents page`);
          navigate('/documents', {
            state: {
              fromNotification: true,
              notificationId: notification.id,
              focusDocumentId: notification.related_entity_id
            }
          });
          break;
          
        default:
          // For other types, don't navigate
          console.log(`ℹ️ No specific route for type: ${notification.related_entity_type}`);
          break;
      }
    }
  };

  return (
    <div style={{
      backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
      borderRadius: '12px',
      padding: '24px',
      border: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0',
      height: '280px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        margin: '0 0 20px 0',
        flexShrink: 0,
        color: mode === 'dark' ? '#ffffff' : '#333333',
        fontFamily: 'Urbanist, Arial, sans-serif'
      }}>
        Notifications
      </h3>

      {/* Notifications List */}
      <div 
        className="scrollable-container"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          flex: 1,
          overflowY: 'auto',
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none' // Internet Explorer/Edge
        }}
      >
        {notifications.length > 0 ? notifications.map((notification, index) => {
          const timeAgo = getTimeAgo(notification.sent_time);
          const avatarInitials = getAvatarInitials(notification.sender);
          
          // Check if notification has known clickable type (same logic as topbar)
          const isClickable = notification.related_entity_id && notification.related_entity_type && 
            ['task', 'project', 'projects', 'document'].includes(notification.related_entity_type.toLowerCase());
          
          return (
            <div
              key={notification.id}
              onClick={isClickable ? () => handleNotificationClick(notification) : undefined}
              style={{
                display: 'flex',
                gap: '12px',
                padding: '16px 0',
                borderBottom: index < notifications.length - 1 ? (mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0') : 'none',
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'background-color 0.2s ease',
                borderRadius: '8px',
                marginLeft: '-8px',
                marginRight: '-8px',
                paddingLeft: '8px',
                paddingRight: '8px'
              }}
              onMouseEnter={isClickable ? (e) => {
                e.currentTarget.style.backgroundColor = mode === 'dark' ? '#252525' : '#f5f5f5';
              } : undefined}
              onMouseLeave={isClickable ? (e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              } : undefined}
            >
              {/* Avatar */}
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                backgroundColor: '#26c3c0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '600',
                color: '#ffffff',
                fontFamily: 'Urbanist, Arial, sans-serif',
                flexShrink: 0
              }}>
                {avatarInitials}
              </div>

              {/* Content */}
              <div style={{ flex: 1 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '6px'
                }}>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    margin: 0,
                    color: mode === 'dark' ? '#ffffff' : '#333333',
                    fontFamily: 'Urbanist, Arial, sans-serif'
                  }}>
                    {notification.title}
                  </h4>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '400',
                    color: '#b0b0b0',
                    fontFamily: 'Urbanist, Arial, sans-serif',
                    whiteSpace: 'nowrap'
                  }}>
                    {timeAgo}
                  </span>
                </div>
                <p style={{
                  fontSize: '12px',
                  fontWeight: '400',
                  color: '#b0b0b0',
                  margin: 0,
                  lineHeight: '1.4',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  wordBreak: 'break-all',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal'
                }}>
                  {notification.message}
                </p>
              </div>
            </div>
          );
        }) : (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100px',
            color: mode === 'dark' ? '#888888' : '#666666',
            fontSize: '14px',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            No notifications found
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPanel;
